"use client";
import { useCallback } from "react";
import { useMenuPermissions } from "@/hooks/useMenuPermissions";
import { usePlanMenus } from "@/hooks/usePlanMenus";

/**
 * useCanSeeMenu — satu sumber aturan "menu ini boleh tampil?" untuk Sidebar DAN Breadcrumb.
 *
 * Visibilitas menu = (diizinkan paket) DAN (IsShow=1 di DB, MUTLAK) DAN (diizinkan role user).
 * (Dipindahkan apa adanya dari Sidebar supaya breadcrumb ikut menyesuaikan sidebar — aga 2026-10-27.)
 *
 * Adopsi Fase 1 sidebar MGMX (cowork/docs/ADOPSI-MGMX-SIDEBAR-PLAN.md):
 *  - Filter IsShow (visibleCodes) — **BERLAKU UNTUK SEMUA USER TERMASUK ADMIN** (keputusan aga 2026-09-24).
 *    Rasional aga: kalau di DB `mgsymmenu.IsShow=0`, itu keputusan bisnis admin — wajib dipatuhi, tanpa bypass.
 *  - Fail-closed: kalau permissions/visibleCodes masih loading (null) → menu ter-gate tak dianggap tampil.
 *  - Admin (level 1) tetap bypass filter role (allowedCodes) — cuma filter IsShow & paket yg mutlak.
 *
 * `kdMenu` tanpa nilai → tidak dibatasi. Array = OR-semantics (cukup 1 kode lolos semua filter).
 */
export function useCanSeeMenu(): (kdMenu?: string | string[]) => boolean {
    const { isAdmin, allowedCodes, visibleCodes } = useMenuPermissions();
    const { planAllows } = usePlanMenus();

    return useCallback((kdMenu?: string | string[]): boolean => {
        if (!kdMenu) return true;                 // tanpa kode → tidak dibatasi

        if (Array.isArray(kdMenu)) {
            // Array = OR-semantics: izinkan kalau MINIMAL 1 kode lolos semua filter
            const allowedByPlan = kdMenu.filter(code => planAllows(code));
            if (allowedByPlan.length === 0) return false;
            // Filter IsShow — MUTLAK untuk semua user termasuk admin
            if (visibleCodes === null) return false;   // FAIL-CLOSED
            const visibleByShow = allowedByPlan.filter(code => visibleCodes.has(code));
            if (visibleByShow.length === 0) return false;
            // Filter role (admin bypass)
            if (isAdmin) return true;
            if (allowedCodes === null) return false;   // FAIL-CLOSED
            return visibleByShow.some(code => allowedCodes.has(code));
        }

        // Plan gating berlaku LEBIH DULU, bahkan untuk admin (paket membatasi tenant, di atas role).
        if (!planAllows(kdMenu)) return false;
        // Filter IsShow — MUTLAK untuk semua user termasuk admin (aga 2026-09-24)
        if (visibleCodes === null) return false;      // FAIL-CLOSED: tunggu fetch selesai
        if (!visibleCodes.has(kdMenu)) return false;  // IsShow=0 di DB → hide untuk SEMUA user
        // Filter role (admin bypass)
        if (isAdmin) return true;                 // admin lihat semua (dalam batas paket + IsShow)
        if (allowedCodes === null) return false;  // FAIL-CLOSED: sebelumnya fail-open (regresi keamanan)
        return allowedCodes.has(kdMenu);
    }, [isAdmin, allowedCodes, visibleCodes, planAllows]);
}
